import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import clsx from "clsx";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

import { Column, Table } from "@fronterahealth/frontera-ui-components";

import { ReportTemplateType, useDeleteReportMutation } from "@api/graphql/types-and-hooks";
import { StatusDefinitionTooltip } from "@components/StatusLegendTooltip/StatusDefinitionTooltip";
import { StatusLegendTooltip } from "@components/StatusLegendTooltip/StatusLegendTooltip";
import { StatusTag } from "@components/StatusTag/StatusTag";
import { TableFilterDropdown } from "@components/TableFilterDropdown/TableFilterDropdown";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { useAdminData } from "@providers/AdminDataProvider";

export const STATUS_VALUES = [
  // Brand new report just started
  "Client Details Needed",

  // Clinical Files are needed
  "Clinical Files Needed",

  // client details filled out
  "At Upload Files",

  // Part 1 Kicked Off
  "Generating Report Page 1",

  // Part 1 has a section that has errored
  "Error on Report Page 1",

  // Part 1 has AI sections generated
  "At Report Page 1",

  // Report Has Prioritize generated
  // NOTE: This may be the same as "At Report Page 1"
  "At Prioritize",

  // STG and Targets Kicked off
  "Generating Short Term Goals",
  "Generating Targets",

  // STG Have Been completed
  "At Short Term Goals",

  // STG has errored
  "Error Generating Short Term Goals",
  "Error Generating Targets",

  // Part 2 Kicked Off
  "Generating Report Page 2",

  // Part 2 has a section that has errored
  "Error on Report Page 2",

  // Part 2 has been generated
  "At Report Page 2",

  "At Review and Sign",

  // Initial Assessment lifecycle — first pass collapses fine-grained
  // in-progress states (Client Details Needed, Generating *, At *, Error *)
  // down to "Draft" at the table level. Report-detail pages still surface
  // the granular state.
  "Draft",
  "Complete",

  // Payor-integrated initial-assessment post-completion lifecycle, sourced from
  // payorreportsubmissionSet.status and its reviews[].status.
  "Submitted",
  "Needs Revision",
  "Approved",

  "Unknown Status",
] as const;
export type StatusType = (typeof STATUS_VALUES)[number];

export interface AssessmentReport {
  id: string;
  clientName: string;
  reportTemplate: ReportTemplateType | null;
  createdBy: string;
  dateCreated: Date;
  lastUpdated: Date;
  status: StatusType;
  redirectRoute: InitialAssessmentPageRoute;
  learnerId?: string;
}

// The IA helper now only emits the simplified lifecycle (Draft/Complete/etc.).
// We expose only those as filter options; the legacy in-progress labels still
// in STATUS_VALUES wouldn't match any current row.
const FILTERABLE_STATUSES: StatusType[] = ["Draft", "Complete", "Submitted", "Needs Revision", "Approved"];

interface AssessmentReportsTableProps {
  reports: (AssessmentReport | null)[];
  onRowClick: (report: AssessmentReport) => void;
  loading?: boolean;
  refetch: () => Promise<unknown>;
  onPageChange: (currentPage: number, itemsPerPage: number) => void;
  pageItems: number;
  totalItemCount: number;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  statusFilter: StatusType[];
  onStatusFilterChange: (next: StatusType[]) => void;
}

export const AssessmentReportsTable: React.FC<AssessmentReportsTableProps> = ({
  reports,
  onRowClick,
  loading,
  refetch,
  onPageChange,
  pageItems,
  totalItemCount,
  searchTerm,
  setSearchTerm,
  statusFilter,
  onStatusFilterChange,
}) => {
  const deleteReportMutation = useDeleteReportMutation({});
  const { "show-report-created-by": showCreatedBy } = useFlags<FeatureFlags>();

  const { roles } = useAdminData();

  const actions = useMemo(() => {
    const isInternalUser = roles.includes("frontera-internal");

    return [
      {
        label: "View",
        callback: (rowData: AssessmentReport) => onRowClick(rowData),
      },
      ...(isInternalUser
        ? [
            {
              label: "Delete",
              textColorClassName: "text-red-700",
              callback: async (rowData: { id: string }) => {
                try {
                  await deleteReportMutation.mutateAsync({ report: rowData.id });
                  await refetch();
                  notifySuccess("Report successfully deleted");
                } catch (err) {
                  console.error("Failed to delete report", err);
                  notifyError("Unable to delete report");
                }
              },
            },
          ]
        : []),
    ];
  }, [roles]);

  const columns = [
    {
      dataIndex: "clientName",
      key: "clientName",
      title: "Client Name",
      className: "w-[22%]",
      render: (value: string) => <span className="ph-no-capture">{value}</span>,
    },
    {
      dataIndex: "reportTemplate" as keyof AssessmentReport,
      key: "reportTemplate",
      title: "Report Template",
      className: "w-[22%]",
      render: (value: ReportTemplateType) => value?.name || "No template",
    },
    ...(showCreatedBy
      ? [
          {
            dataIndex: "createdBy" as keyof AssessmentReport,
            key: "createdBy",
            title: "Created By",
            className: "w-[16%]",
          },
        ]
      : []),
    {
      dataIndex: "dateCreated",
      key: "dateCreated",
      title: "Date Created",
      className: "w-[12%]",
      render: (value: Date) => (value ? format(value, "MM/dd/yyyy") : ""),
    },
    {
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      title: "Last Updated",
      className: "w-[12%]",
      render: (value: Date) => (value ? format(value, "MM/dd/yyyy") : ""),
    },
    {
      dataIndex: "status",
      key: "status",
      title: (
        <span className="inline-flex items-center gap-1">
          Status
          <StatusLegendTooltip />
        </span>
      ),
      className: "w-[14%]",
      render: (value: string) => (
        <StatusDefinitionTooltip status={value}>
          <StatusTag status={value} />
        </StatusDefinitionTooltip>
      ),
    },
  ];

  const statusFilterItems = useMemo(() => FILTERABLE_STATUSES.map((status) => ({ id: status, label: status })), []);

  const statusFilterDropdown = (
    <TableFilterDropdown<StatusType>
      label="Status"
      items={statusFilterItems}
      value={statusFilter}
      onChange={onStatusFilterChange}
      headerSlot={<StatusLegendTooltip />}
    />
  );

  // Filtering happens client-side over the current page, so paginating across
  // server pages would skip filtered-out rows on intervening pages and land on
  // empty pages. Hide pagination while a filter narrows the visible set.
  const isFiltering = statusFilter.length > 0;

  return (
    <div
      className={clsx(
        "mt-4 w-full [&_.flow-root>div>div>div]:min-h-[480px] [&_table]:!table-fixed [&_td>.text-center]:min-h-[300px]",
        // mt-auto pins pagination to the bottom of the 480px frame when rows
        // are sparse. With pagination hidden, that same rule pushes the table
        // itself to the bottom and leaves a giant gap above — only apply it
        // when pagination is visible.
        !isFiltering && "[&_.flow-root>div>div>div>:last-child]:mt-auto",
      )}
    >
      <Table
        dataTestId={"assessment-reports-table"}
        onRowClick={onRowClick}
        onPageChange={onPageChange}
        loading={loading}
        defaultItemsPerPage={pageItems}
        totalItemCount={totalItemCount}
        showSearch
        inputClassName="w-96 py-2.5"
        showPagination={!isFiltering}
        itemsPerPageOptions={[5, 10, 20, 50]}
        actions={actions}
        data={reports as AssessmentReport[]}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        columns={columns as Column<AssessmentReport>[]}
        headerContent={statusFilterDropdown}
      />
    </div>
  );
};
